// src/components/Separation/ResignationSubmitModal.tsx

import React, { useState } from 'react';
import { toast } from "sonner";
import { submitResignation } from '../../api/separation';

interface ResignationSubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ResignationSubmitModal: React.FC<ResignationSubmitModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    resignation_date: new Date().toISOString().split('T')[0],
    requested_last_day: '',
    notice_period_days: 30,
    reason: '',
    additional_notes: '',
    early_release_requested: false,
    early_release_reason: '',
  });

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.requested_last_day) {
      toast.error('Please provide your last working day');
      return;
    }
    if (!formData.reason) {
      toast.error('Please provide a reason for resignation');
      return;
    }

    setLoading(true);
    try {
      await submitResignation(formData);
      toast.success('Resignation submitted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit resignation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header and footer sit OUTSIDE the scroll area. As sticky children of
              it they were painted under the scrolling fields — the global theme
              positions form controls, and a z-index:auto sticky box loses that
              tie-break to later siblings. */}
          <div className="shrink-0 z-10 bg-white px-6 py-4 border-b border-gray-200 shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Submit Resignation</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resignation Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.resignation_date}
                onChange={(e) => setFormData(prev => ({ ...prev, resignation_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requested Last Working Day <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.requested_last_day}
                onChange={(e) => setFormData(prev => ({ ...prev, requested_last_day: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notice Period (Days)
              </label>
              <input
                type="number"
                value={formData.notice_period_days}
                onChange={(e) => setFormData(prev => ({ ...prev, notice_period_days: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Resignation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Please provide your reason for resignation..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.additional_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional information..."
              />
            </div>

            <div className="border rounded-lg p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.early_release_requested}
                  onChange={(e) => setFormData(prev => ({ ...prev, early_release_requested: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Request Early Release</span>
              </label>
              
              {formData.early_release_requested && (
                <textarea
                  value={formData.early_release_reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, early_release_reason: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Reason for early release request..."
                />
              )}
            </div>
          </div>

          <div className="shrink-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Resignation'}
            </button>
            <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResignationSubmitModal;